import React, { useState, useEffect, useRef } from "react";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { Card, Chip, Grid, IconButton, InputLabel, Tooltip } from "@mui/material";
import MDSelect from "components/MDSelect";
import DataTable from "examples/Tables/DataTable";
import { get, post, put, delNoResponse, getFile, postNoResponse, postUploadNoResponse } from "utils/WebService";
import Icon from "@mui/material/Icon";
import Swal from "sweetalert2";
import { useMaterialUIController } from "context";
import { PARAMETROS_SQL_AJUDA } from "./relatoriosProntos";
import { ESCURO } from "assets/coresTema";

function ReportsCustom() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const [reports, setReports] = useState([]);
    const [editingReport, setEditingReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const tableState = useRef({});
    const fetchData = () => {};
    const fileInputRef = useRef(null);
    const [uploadingId, setUploadingId] = useState(null);
    // Equipes que o usuário pode vincular (as suas, se está em equipe), com as carteiras de cada uma
    const [equipesOpcoes, setEquipesOpcoes] = useState([]);
    const [restritoEquipe, setRestritoEquipe] = useState(false);

    useEffect(() => {
        loadReports();
        get("/relatorio-customizado/equipes")
            .then(res => {
                setEquipesOpcoes(Array.isArray(res?.equipes) ? res.equipes : []);
                setRestritoEquipe(Boolean(res?.restritoEquipe));
            })
            .catch(() => setEquipesOpcoes([]));
    }, []);

    // Relatório novo de quem está em equipe já nasce das suas equipes (sem equipe, apareceria para todos)
    const novoRelatorio = () => setEditingReport({
        ccNome: "", ccDescricao: "", ccQuerySql: "",
        equipeIds: restritoEquipe ? equipesOpcoes.map(e => e.id) : [],
    });

    // Opções do campo: as equipes que o usuário pode vincular e as que o relatório já tem (mesmo de outra equipe)
    const opcoesEquipe = (relatorio) => {
        const opcoes = equipesOpcoes.map(e => ({ value: e.id, label: e.nome, carteiras: e.carteiras || [] }));
        (relatorio?.equipeIds || []).forEach((id, i) => {
            if (!opcoes.some(o => o.value === id)) {
                opcoes.push({ value: id, label: (relatorio.equipeNomes || [])[i] || `Equipe ${id}`, carteiras: [] });
            }
        });
        return opcoes;
    };

    const loadReports = async () => {
        setLoading(true);
        try {
            const res = await get("/relatorio-customizado");
            setReports(res.data || res || []);
        } catch (error) {
            Swal.fire("Erro", "Falha ao carregar relatórios", "error");
        }
        setLoading(false);
    };

    const handleSave = async () => {
        if (!editingReport.ccNome || !editingReport.ccQuerySql) {
            Swal.fire("Aviso", "Nome e SQL são obrigatórios", "warning");
            return;
        }
        try {
            if (editingReport.cvId) {
                await put(`/relatorio-customizado/${editingReport.cvId}`, editingReport);
                Swal.fire("Sucesso", "Relatório atualizado!", "success");
            } else {
                await post("/relatorio-customizado", editingReport);
                Swal.fire("Sucesso", "Relatório criado!", "success");
            }
            setEditingReport(null);
            loadReports();
        } catch (error) {
            // Ex.: sem permissão (c072_U) ou SQL que não é só leitura: o motivo vem do backend
            Swal.fire("Erro", error?.message || "Falha ao salvar relatório", "error");
        }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: "Tem certeza?",
            text: "Você não poderá reverter isso!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Sim, excluir!",
            cancelButtonText: "Cancelar"
        });
        if (result.isConfirmed) {
            try {
                await delNoResponse(`/relatorio-customizado/${id}`);
                Swal.fire("Excluído!", "Relatório excluído com sucesso.", "success");
                loadReports();
            } catch (error) {
                Swal.fire("Erro", error?.message || "Falha ao excluir", "error");
            }
        }
    };

    const handleExport = async (id, name) => {
        setLoading(true);
        try {
            await getFile(`/relatorio-customizado/${id}/export`, `${name}.xlsx`);
        } catch (error) {
            // Ex.: "Este relatório precisa do filtro: Data inicial" (gere pela tela Relatórios e Indicadores, com os filtros)
            Swal.fire("Erro", error?.message || "Falha ao gerar a planilha.", "error");
        }
        setLoading(false);
    };

    // Sem modelo próprio o relatório sai na planilha padrão do sistema
    const handleRemoverTemplate = async (relatorio) => {
        const result = await Swal.fire({
            title: "Voltar à planilha padrão?",
            text: `"${relatorio.ccNome}" deixa de usar o modelo ${relatorio.ccTemplateArquivo} e passa a sair na planilha padrão do sistema. O arquivo do modelo continua na pasta.`,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Usar o padrão",
            cancelButtonText: "Cancelar"
        });
        if (!result.isConfirmed) return;
        try {
            await postNoResponse(`/relatorio-customizado/${relatorio.cvId}/remover-template`);
            loadReports();
        } catch (error) {
            Swal.fire("Erro", error?.message || "Falha ao remover o modelo", "error");
        }
    };

    const handleUploadTemplateClick = (id) => {
        setUploadingId(id);
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleFileChange = async (event) => {
        const file = event.target.files[0];
        if (!file || !uploadingId) return;

        setLoading(true);
        const formData = new FormData();
        formData.append("file", file);

        try {
            await postUploadNoResponse(`/relatorio-customizado/${uploadingId}/upload-template`, formData);
            Swal.fire("Sucesso", "Template anexado com sucesso!", "success");
            loadReports();
        } catch (error) {
            Swal.fire("Erro", error?.message || "Falha ao enviar template", "error");
        } finally {
            setUploadingId(null);
            setLoading(false);
            event.target.value = null; // reset input
        }
    };

    const columns = [
        { header: "ID", accessorKey: "cvId", size: 10 },
        { header: "Nome", accessorKey: "ccNome", size: 35 },
        { header: "Descrição", accessorKey: "ccDescricao", size: 25 },
        {
            header: "Equipes",
            accessorKey: "equipeNomes",
            size: 15,
            cell: ({ row }) => {
                const nomes = row.original.equipeNomes || [];
                return (
                    <Tooltip title={nomes.length ? "Só quem está nessas equipes vê o relatório (e quem não está em nenhuma equipe)" : "Sem equipe: aparece para todos"}>
                        <MDTypography variant="caption" color="text">{nomes.length ? nomes.join(", ") : "Todas"}</MDTypography>
                    </Tooltip>
                );
            },
        },
        {
            header: "Planilha",
            accessorKey: "ccTemplateArquivo",
            size: 15,
            cell: ({ row }) => (
                <Tooltip title={row.original.ccTemplateArquivo ? `Modelo: ${row.original.ccTemplateArquivo}` : "Título, filtros usados, valores formatados, totais e filtro do Excel"}>
                    <Chip
                        size="small"
                        icon={<Icon sx={{ fontSize: "15px !important" }}>{row.original.ccTemplateArquivo ? "description" : "table_view"}</Icon>}
                        label={row.original.ccTemplateArquivo ? "Modelo próprio" : "Padrão do sistema"}
                        sx={{
                            color: darkMode ? "#FFFFFF" : "#344767",
                            backgroundColor: row.original.ccTemplateArquivo
                                ? (darkMode ? "rgba(255, 183, 77, 0.2)" : "#FFF3E0")
                                : (darkMode ? "rgba(129, 199, 132, 0.2)" : "#E8F5E9"),
                            "& .MuiChip-icon": { color: row.original.ccTemplateArquivo ? (darkMode ? "#FFB74D" : "#B26A00") : (darkMode ? "#81C784" : "#2E7D32") }
                        }}
                    />
                </Tooltip>
            ),
        },
        {
            header: "Ações",
            accessorKey: "actions",
            size: 20,
            cell: ({ row }) => (
                <MDBox display="flex" alignItems="center">
                    <Tooltip title="Editar">
                        <IconButton color="info" onClick={() => setEditingReport(row.original)}>
                            <Icon>edit</Icon>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={row.original.ccTemplateArquivo ? "Trocar o modelo próprio (.xlsx)" : "Anexar modelo próprio (.xlsx), opcional"}>
                        <IconButton color="warning" onClick={() => handleUploadTemplateClick(row.original.cvId)}>
                            <Icon>upload_file</Icon>
                        </IconButton>
                    </Tooltip>
                    {row.original.ccTemplateArquivo && (
                        <Tooltip title="Remover o modelo próprio e voltar à planilha padrão">
                            <IconButton color="secondary" onClick={() => handleRemoverTemplate(row.original)}>
                                <Icon>layers_clear</Icon>
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title="Baixar planilha">
                        <IconButton color="success" onClick={() => handleExport(row.original.cvId, row.original.ccNome)}>
                            <Icon>download</Icon>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                        <IconButton color="error" onClick={() => handleDelete(row.original.cvId)}>
                            <Icon>delete</Icon>
                        </IconButton>
                    </Tooltip>
                </MDBox>
            ),
        },
    ];

    return (
        <Grid container display="flex" justifyContent="center">
            <Grid item xs={12} md={12}>
                <Card
                            variant="outlined"
                            sx={{
                                p: 2,
                                background: darkMode ? ESCURO.cartao : "#fff",
                                borderColor: darkMode ? ESCURO.divisoria : "#ddd",
                            }}
                        >
                            {!editingReport && (
                                <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={2} p={1}>
                                    <MDTypography variant="caption" sx={{ color: darkMode ? "rgba(255,255,255,0.8)" : "#6c757d", maxWidth: 720 }}>
                                        Os relatórios ativos aparecem em Relatórios e Indicadores, com os filtros que o SQL usa. A planilha sai no padrão do sistema; o modelo próprio (.xlsx) é opcional.
                                    </MDTypography>
                                    <MDButton variant="contained" color="dark" onClick={novoRelatorio}>
                                        <Icon sx={{ mr: 1 }}>add</Icon> Novo Relatório
                                    </MDButton>
                                </MDBox>
                            )}
                            <MDBox p={1}>
                                {editingReport ? (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <MDInput
                                                label="Nome do Relatório"
                                                fullWidth
                                                value={editingReport.ccNome || ""}
                                                onChange={(e) => setEditingReport({ ...editingReport, ccNome: e.target.value })}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <MDInput
                                                label="Descrição"
                                                fullWidth
                                                value={editingReport.ccDescricao || ""}
                                                onChange={(e) => setEditingReport({ ...editingReport, ccDescricao: e.target.value })}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Equipes</InputLabel>
                                            <MDSelect
                                                isMulti
                                                name="equipes"
                                                value={opcoesEquipe(editingReport).filter(o => (editingReport.equipeIds || []).includes(o.value))}
                                                options={opcoesEquipe(editingReport)}
                                                onChange={(opts) => setEditingReport({ ...editingReport, equipeIds: (opts || []).map(o => o.value) })}
                                                placeholder="Todas as equipes"
                                            />
                                            <MDTypography variant="caption" display="block" mt={0.5} sx={{ color: darkMode ? "rgba(255,255,255,0.8)" : "#6c757d", px: 1 }}>
                                                {(editingReport.equipeIds || []).length
                                                    ? "Só quem está nessas equipes vê, roda e edita o relatório (quem não está em nenhuma equipe vê todos)."
                                                    : "Sem equipe: o relatório aparece para todos."}
                                            </MDTypography>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Carteiras dessas equipes</InputLabel>
                                            <MDTypography variant="caption" display="block" sx={{ color: darkMode ? "rgba(255,255,255,0.8)" : "#6c757d", px: 1, pt: 1 }}>
                                                {(() => {
                                                    const escolhidas = opcoesEquipe(editingReport).filter(o => (editingReport.equipeIds || []).includes(o.value));
                                                    const carteiras = [...new Set(escolhidas.flatMap(o => o.carteiras))].sort((a, b) => a.localeCompare(b));
                                                    if (!escolhidas.length) return "Todas. Os dados de cada usuário continuam limitados às carteiras das equipes dele (:escopoCarteiras / :carteiraIds no SQL).";
                                                    return carteiras.length
                                                        ? `${carteiras.join(", ")}. Os dados continuam limitados às carteiras de quem gera (:escopoCarteiras / :carteiraIds no SQL).`
                                                        : "Equipe sem carteira vinculada.";
                                                })()}
                                            </MDTypography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <MDInput
                                                label="Query SQL (ex: SELECT * FROM Tabela WHERE dtCriacao >= :startDate AND idUsuario IN (:userIds) )"
                                                fullWidth
                                                multiline
                                                rows={10}
                                                value={editingReport.ccQuerySql || ""}
                                                onChange={(e) => setEditingReport({ ...editingReport, ccQuerySql: e.target.value })}
                                            />
                                            <MDTypography variant="caption" display="block" mt={0.5} sx={{ color: darkMode ? "rgba(255,255,255,0.8)" : "#6c757d" }}>
                                                Parâmetros que viram filtros na tela Relatórios e Indicadores: {PARAMETROS_SQL_AJUDA}. Cliente e carteira em branco = todos; os demais parâmetros usados são obrigatórios para gerar. Só uma consulta de leitura (SELECT ou WITH): o sistema recusa SQL que altera o banco ou executa procedimentos.
                                            </MDTypography>
                                        </Grid>
                                        <Grid item xs={12} display="flex" justifyContent="flex-end">
                                            <MDButton color="secondary" onClick={() => setEditingReport(null)} sx={{ mr: 2 }}>
                                                Cancelar
                                            </MDButton>
                                            <MDButton color="info" onClick={handleSave}>
                                                Salvar Relatório
                                            </MDButton>
                                        </Grid>
                                    </Grid>
                                ) : (
                                    <DataTable
                                        columnData={columns}
                                        data={reports}
                                        tableState={tableState}
                                        fetchData={fetchData}
                                        showTotalEntries={false}
                                        noEndBorder
                                    />
                                )}
                            </MDBox>
                        </Card>
            </Grid>
            <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                accept=".xlsx"
                onChange={handleFileChange}
            />
        </Grid>
    );
}

export default ReportsCustom;
